/**
 * Configuration Service
 * 
 * Service pour charger la configuration depuis un fichier externe
 * permettant de modifier les paramètres après le build de production.
 */

import type { AppConfig } from '../types/config'
import configValidator from '../utils/configValidator'
import { showApiWarningNotification } from '../utils/globalNotifications'

interface ExternalConfig {
  CLIENT_ID?: string
  SCOPES?: string
  REDIRECT_URI?: string
  USE_MOCK_SERVICES?: boolean
  ENABLE_DEVTOOLS?: boolean
  API_BASE_URL?: string
  API_TIMEOUT?: number
  ENVIRONMENT?: 'development' | 'staging' | 'production'
  VERSION?: string
  BUILD_DATE?: string
  FEATURES?: {
    enableAnalytics?: boolean
    enableReporting?: boolean
    enableFileUpload?: boolean
    enableRealTimeUpdates?: boolean
    enableAdvancedSearch?: boolean
    enableExport?: boolean
    enableImport?: boolean
    betaFeatures?: boolean
  }
  OAUTH?: {
    clientId?: string
    redirectUri?: string
    scope?: string[]
    responseType?: 'code' | 'token'
    flowType?: 'authorization_code' | 'implicit'
  }
  STORAGE?: {
    prefix?: string
    enableEncryption?: boolean
    defaultExpiry?: number
    maxSize?: number
  }
  I18N?: {
    defaultLocale?: string
    supportedLocales?: string[]
    fallbackLocale?: string
    loadPath?: string
  }
}

class ConfigService {
  private _config: AppConfig | null = null
  private _isLoaded: boolean = false
  private _loadPromise: Promise<AppConfig> | null = null

  /**
   * Charge la configuration depuis le fichier config.json
   */
  async loadConfig(): Promise<AppConfig> {
    if (this._loadPromise) {
      return this._loadPromise
    }

    this._loadPromise = this._fetchConfig()
    return this._loadPromise
  }

  /**
   * Récupère la configuration externe depuis config.json
   */
  private async _fetchConfig(): Promise<AppConfig> {
    try {
      console.log('🔧 Chargement de la configuration externe...')
      const response = await fetch('/config.json')
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const externalConfig: ExternalConfig = await response.json()
      console.log('✅ Configuration externe chargée:', externalConfig)
      
      // Build the full configuration with defaults
      this._config = this._buildConfigWithDefaults(externalConfig)

      // Validate configuration
      configValidator.validateAndReport(this._config!, false)

      this._isLoaded = true
      return this._config!
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('❌ Unable to load config.json:', errorMessage)
      console.error('💡 Ensure config.json exists in the public/ folder')
      
      // Show user notification for config loading failures
      showApiWarningNotification(
        'Configuration file could not be loaded. Using default settings.'
      )
      
      // Use default values only if config.json is not available
      this._config = configValidator.getDefaultConfig() as AppConfig
      
      console.warn('⚠️ Using default configuration values')
      configValidator.validateAndReport(this._config!, false)

      this._isLoaded = true
      return this._config!
    }
  }

  /**
   * Build configuration with defaults from external config
   */
  private _buildConfigWithDefaults(externalConfig: ExternalConfig): AppConfig {
    return {
      API_BASE_URL: externalConfig.API_BASE_URL || '/api',
      API_TIMEOUT: externalConfig.API_TIMEOUT || 30000,
      ENABLE_DEVTOOLS: externalConfig.ENABLE_DEVTOOLS !== undefined ? externalConfig.ENABLE_DEVTOOLS : false,
      ENVIRONMENT: externalConfig.ENVIRONMENT || 'development',
      VERSION: externalConfig.VERSION || '1.0.0',
      BUILD_DATE: externalConfig.BUILD_DATE || new Date().toISOString(),
      FEATURES: {
        enableAnalytics: externalConfig.FEATURES?.enableAnalytics ?? false,
        enableReporting: externalConfig.FEATURES?.enableReporting ?? true,
        enableFileUpload: externalConfig.FEATURES?.enableFileUpload ?? true,
        enableRealTimeUpdates: externalConfig.FEATURES?.enableRealTimeUpdates ?? false,
        enableAdvancedSearch: externalConfig.FEATURES?.enableAdvancedSearch ?? true,
        enableExport: externalConfig.FEATURES?.enableExport ?? true,
        enableImport: externalConfig.FEATURES?.enableImport ?? true,
        betaFeatures: externalConfig.FEATURES?.betaFeatures ?? false
      },
      OAUTH: {
        clientId: externalConfig.OAUTH?.clientId || externalConfig.CLIENT_ID || 'browser',
        redirectUri: externalConfig.OAUTH?.redirectUri || externalConfig.REDIRECT_URI || '/callback',
        scope: externalConfig.OAUTH?.scope || (externalConfig.SCOPES ? externalConfig.SCOPES.split(' ') : ['openid', 'offline_access']),
        responseType: externalConfig.OAUTH?.responseType || 'code',
        flowType: externalConfig.OAUTH?.flowType || 'authorization_code'
      },
      STORAGE: {
        prefix: externalConfig.STORAGE?.prefix || 'archiweb_',
        enableEncryption: externalConfig.STORAGE?.enableEncryption ?? false,
        defaultExpiry: externalConfig.STORAGE?.defaultExpiry || 86400000, // 24 hours
        maxSize: externalConfig.STORAGE?.maxSize || 5242880 // 5MB
      },
      I18N: {
        defaultLocale: externalConfig.I18N?.defaultLocale || 'en',
        supportedLocales: externalConfig.I18N?.supportedLocales || ['en', 'fr', 'ar'],
        fallbackLocale: externalConfig.I18N?.fallbackLocale || 'en',
        loadPath: externalConfig.I18N?.loadPath || '/locales/{{lng}}.json'
      }
    }
  }

  /**
   * Obtient la configuration actuelle (charge si nécessaire)
   */
  async getConfig(): Promise<AppConfig> {
    if (!this._isLoaded) {
      await this.loadConfig()
    }
    return this._config!
  }

  /**
   * Obtient une valeur de configuration spécifique
   */
  async get<K extends keyof AppConfig>(key: K): Promise<AppConfig[K]> {
    const config = await this.getConfig()
    return config[key]
  }

  /**
   * Recharge la configuration (utile pour les mises à jour à chaud)
   */
  async reload(): Promise<AppConfig> {
    this._config = null
    this._isLoaded = false
    this._loadPromise = null
    return await this.loadConfig()
  }

  /**
   * Vérifie si la configuration est chargée
   */
  isLoaded(): boolean {
    return this._isLoaded
  }

  /**
   * Gets the current configuration synchronously (throws if not loaded)
   */
  getConfigSync(): AppConfig {
    if (!this._isLoaded || !this._config) {
      throw new Error('Configuration not loaded. Call loadConfig() first.')
    }
    return this._config
  }

  /**
   * Type-safe access to nested configuration values
   */
  async getFeature(feature: keyof AppConfig['FEATURES']): Promise<boolean> {
    const config = await this.getConfig()
    return config.FEATURES[feature]
  }

  /**
   * Type-safe access to OAuth configuration
   */
  async getOAuthConfig(): Promise<AppConfig['OAUTH']> {
    const config = await this.getConfig()
    return config.OAUTH
  }

  /**
   * Type-safe access to storage configuration
   */
  async getStorageConfig(): Promise<AppConfig['STORAGE']> {
    const config = await this.getConfig()
    return config.STORAGE
  }

  /**
   * Type-safe access to i18n configuration
   */
  async getI18nConfig(): Promise<AppConfig['I18N']> {
    const config = await this.getConfig()
    return config.I18N
  }
}

// Instance singleton
const configService = new ConfigService()

export default configService
export type { ExternalConfig }
